/**
 * AiStaffChatPanel — floating AI assistant panel for clinic staff.
 *
 * Rendered in Layout.tsx as a global overlay. Sends messages to
 * POST /api/ai/chat/message and displays the conversation inline.
 * Supports /patient-history <patientId> slash commands.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, IconButton,
  Tooltip, CircularProgress, Alert, Chip, Divider, Fade,
} from '@mui/material';
import { Bot, X, Send, Minus, RotateCcw, Info, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { aiStaffChat } from '../../lib/api/modules/aiFeatures';
import { usePatientDetails } from '../../contexts/PatientDetailsContext';
import { useTranslation } from 'react-i18next';

interface LocalMessage {
  id: string;
  role: 'USER' | 'ASSISTANT';
  content: string;
  createdAt: string;
}

/**
 * Renders an assistant reply as formatted markdown (headings, bold, bullet lists, inline code, etc.)
 * instead of a wall of plain text with literal `**`/`-` characters. Backend responses (deterministic
 * inventory/OPD summaries, RAG answers, clinical references) are authored in markdown — this is where
 * that actually gets rendered. Sized and colored for the compact chat bubble it lives in, following
 * the same nested-selector styling pattern used for AI content elsewhere (PatientAISummaryPanel).
 */
function AssistantMarkdown({ content }: { content: string }) {
  return (
    <Box
      sx={{
        fontSize: '0.8125rem',
        lineHeight: 1.55,
        wordBreak: 'break-word',
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        '& p': { m: 0, mb: 1 },
        '& ul, & ol': { m: 0, mb: 1, pl: 2.25 },
        '& li': { mb: 0.375 },
        '& li > p': { mb: 0 },
        '& strong': { fontWeight: 700 },
        '& em': { fontStyle: 'italic' },
        '& h1, & h2, & h3': {
          fontSize: '0.8rem', fontWeight: 700, mt: 1.25, mb: 0.5,
          textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.primary',
        },
        '& hr': { my: 1, border: 0, borderTop: '1px solid', borderColor: 'divider', opacity: 0.5 },
        '& blockquote': {
          m: 0, mb: 1, pl: 1.25, borderLeft: '2px solid', borderColor: 'divider',
          color: 'text.secondary', fontStyle: 'italic',
        },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.85em', bgcolor: 'action.selected', borderRadius: 0.5, px: 0.5, py: 0.125,
        },
        '& pre': {
          bgcolor: 'action.selected', borderRadius: 1, p: 1, mb: 1, overflowX: 'auto',
          '& code': { bgcolor: 'transparent', p: 0 },
        },
        '& a': { color: 'primary.main', textDecorationColor: 'currentColor' },
        '& table': { borderCollapse: 'collapse', mb: 1, fontSize: '0.8em' },
        '& th, & td': { border: '1px solid', borderColor: 'divider', px: 0.75, py: 0.375, textAlign: 'left' },
      }}
    >
      <ReactMarkdown>{content}</ReactMarkdown>
    </Box>
  );
}

interface AiStaffChatPanelProps {
  open: boolean;
  onClose: () => void;
}

export function AiStaffChatPanel({ open, onClose }: AiStaffChatPanelProps) {
  const { t } = useTranslation('ui');
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scope the assistant to a patient when exactly one patient window is open and visible — that's the
  // unambiguous "patient in context" for patient-aware answers and "book for this patient".
  const { openPatients } = usePatientDetails();
  const activePatient = useMemo(() => {
    const visible = Array.from(openPatients.entries()).filter(([, e]) => !e.isMinimized);
    if (visible.length !== 1) return null;
    const [id, entry] = visible[0];
    return { id, name: entry.patient?.name as string | undefined };
  }, [openPatients]);
  // A patient window (right-anchored drawer) is on screen — move the chat to the left so they don't overlap.
  const patientWindowOpen = useMemo(
    () => Array.from(openPatients.values()).some((e) => !e.isMinimized),
    [openPatients]
  );

  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, minimized]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: LocalMessage = {
      id: `u-${Date.now()}`,
      role: 'USER',
      content: text,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    setError(null);

    try {
      const res = await aiStaffChat.sendMessage(text, activePatient?.id);
      if (res.data) {
        const { response, sessionId: sid, disclaimer: disc, success, errorMessage } = res.data;
        if (sid) setSessionId(sid);
        if (disc) setDisclaimer(disc);
        if (success) {
          const assistantMsg: LocalMessage = {
            id: `a-${Date.now()}`,
            role: 'ASSISTANT',
            content: response,
            createdAt: new Date().toISOString(),
          };
          setMessages(prev => [...prev, assistantMsg]);
        } else {
          setError(errorMessage ?? 'AI assistant is temporarily unavailable.');
        }
      } else {
        setError(res.error ?? 'Failed to send message.');
      }
    } catch {
      setError('Failed to reach the AI assistant.');
    } finally {
      setSending(false);
    }
  }, [input, sending, activePatient?.id]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = useCallback(async () => {
    if (sessionId) {
      await aiStaffChat.endSession(sessionId).catch(() => {});
      setSessionId(null);
    }
    setMessages([]);
    setError(null);
    setInput('');
  }, [sessionId]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        // Sit above the patient drawer (also zIndex 1300) so the chat is never hidden behind it,
        // and shift to the left edge when a patient window is open so both stay fully visible.
        zIndex: 1401,
        ...(patientWindowOpen
          ? { left: 24, right: 'auto' }
          : { right: 24, left: 'auto' }),
        width: minimized ? 'auto' : { xs: '92vw', sm: 400 },
      }}
    >
      <Fade in={open}>
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              cursor: minimized ? 'pointer' : 'default',
            }}
            onClick={minimized ? () => setMinimized(false) : undefined}
          >
            <Bot size={18} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
              {t('aiAssistant')}
            </Typography>
            {!minimized && (
              <>
                <Tooltip title={t('newConversation')}>
                  <IconButton size="small" onClick={clearConversation} sx={{ color: 'inherit', p: 0.5 }}>
                    <RotateCcw size={15} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('minimise')}>
                  <IconButton size="small" onClick={() => setMinimized(true)} sx={{ color: 'inherit', p: 0.5 }}>
                    <Minus size={15} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('close')}>
              <IconButton size="small" onClick={onClose} sx={{ color: 'inherit', p: 0.5 }}>
                <X size={15} />
              </IconButton>
            </Tooltip>
          </Stack>

          {!minimized && (
            <>
              {/* Patient-context indicator — the assistant answers about this patient and can book for them */}
              {activePatient && (
                <Box sx={{ px: 1.5, pt: 1 }}>
                  <Chip
                    size="small"
                    icon={<User size={13} />}
                    label={`Patient context: ${activePatient.name || 'selected patient'}`}
                    color="primary"
                    variant="outlined"
                    sx={{ maxWidth: '100%' }}
                  />
                </Box>
              )}
              {/* Message list */}
              <Box
                sx={{
                  height: 360,
                  overflowY: 'auto',
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {messages.length === 0 && (
                  <Box sx={{ m: 'auto', textAlign: 'center', opacity: 0.5 }}>
                    <Bot size={32} />
                    <Typography variant="body2" mt={1}>
                      {t('askMeAnythingAboutClinicalWorkflows')}
                    </Typography>
                    <Typography variant="caption" display="block" mt={0.5}>
                      {t('tipType')} <code>/patient-history &lt;id&gt;</code> {t('forAPatientCaseBrief')}
                    </Typography>
                  </Box>
                )}

                {messages.map(msg => (
                  <Box
                    key={msg.id}
                    sx={{
                      alignSelf: msg.role === 'USER' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: msg.role === 'USER' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        bgcolor: msg.role === 'USER' ? 'primary.main' : 'action.hover',
                        color: msg.role === 'USER' ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      {msg.role === 'ASSISTANT' ? (
                        <AssistantMarkdown content={msg.content} />
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.content}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                ))}

                {sending && (
                  <Box alignSelf="flex-start">
                    <CircularProgress size={16} />
                  </Box>
                )}

                {error && (
                  <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: 12 }}>
                    {error}
                  </Alert>
                )}

                <div ref={bottomRef} />
              </Box>

              <Divider />

              {/* Disclaimer */}
              {disclaimer && (
                <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Info size={11} style={{ marginTop: 2, flexShrink: 0, opacity: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {disclaimer}
                  </Typography>
                </Box>
              )}

              {/* Input */}
              <Box sx={{ px: 1.5, py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    inputRef={inputRef}
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    placeholder={t('askAQuestionShiftEnterFor')}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={sending}
                    sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
                  />
                  <IconButton
                    color="primary"
                    onClick={sendMessage}
                    disabled={sending || !input.trim()}
                    size="small"
                    sx={{ mb: 0.25 }}
                  >
                    {sending ? <CircularProgress size={16} /> : <Send size={16} />}
                  </IconButton>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Fade>
    </Box>
  );
}

/** Floating trigger button shown in Layout when chat panel is closed. */
export function AiChatFab({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('ui');
  return (
    <Tooltip title={t('aiAssistant')}>
      <IconButton
        onClick={onClick}
        sx={{
          color: 'text.secondary',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <Bot size={18} />
      </IconButton>
    </Tooltip>
  );
}
